// Marketplace Phase 1 — Demand Insights admin page (Feb 2026).
//
// Path: /admin/marketplace/demand
// Roles: admin, staff only. Backend enforces 403 and additionally
// returns 404 when MARKETPLACE_ENABLED is off, so this page is
// defensively wired to render an "Insufficient sample" or "Module
// disabled" state instead of crashing.
//
// Wiring:
//   • GET  /api/marketplace/demand/latest         — hero + 3 tables + unknown panel
//   • GET  /api/marketplace/demand/history?limit=2 — previous snapshot for week-over-week Δ
//   • POST /api/marketplace/demand/run            — admin-only "Regenerate snapshot"
//
// Privacy invariants surfaced visually:
//   • No user_id ever rendered.
//   • Insufficient-data banner when total_users_included < 50.
//   • K-anon floor (5) advertised in the privacy banner.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, PlayCircle, TrendingUp, ShieldCheck, Globe2 } from "lucide-react";
import DemandSummaryHero from "@/components/admin/marketplace/DemandSummaryHero";
import DemandTable from "@/components/admin/marketplace/DemandTable";
import UnknownTermsPanel from "@/components/admin/marketplace/UnknownTermsPanel";
import DemandReadinessPanel from "@/components/admin/marketplace/DemandReadinessPanel";
import WlpAdoptionPanel from "@/components/admin/marketplace/WlpAdoptionPanel";

// Translate a slug like "head-coach" → "Head Coach" for human readability.
// We deliberately do not hit a catalog endpoint: the slug is the public
// label in Phase 1 and any catalog-vs-snapshot mismatch is something
// the admin should see literally.
const friendlyLabel = (slug) => {
  if (!slug) return "—";
  return String(slug)
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export default function MarketplaceDemand() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [snapshot, setSnapshot] = useState(null);
  const [prevSnapshot, setPrevSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setDisabled(false);
    try {
      const [latestRes, historyRes] = await Promise.all([
        api.get("/marketplace/demand/latest"),
        api.get("/marketplace/demand/history?limit=2"),
      ]);
      const snap = latestRes.data?.snapshot ?? null;
      const items = historyRes.data?.items ?? [];
      setSnapshot(snap);
      // history[0] is the latest (same as snap); use items[1] as the
      // previous snapshot for the Δ column.
      setPrevSnapshot(items.length > 1 ? items[1] : null);
    } catch (err) {
      if (err?.response?.status === 404) {
        setDisabled(true);
      } else {
        toast.error(t("marketplace.demand.loadFailed"));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const runSnapshot = useCallback(async () => {
    setRunning(true);
    setConfirmOpen(false);
    try {
      const res = await api.post("/marketplace/demand/run");
      if (res.data?.snapshot) {
        // Cheap optimistic update — full reload still happens to refresh
        // history (for the Δ column).
        setSnapshot(res.data.snapshot);
      }
      toast.success(t("marketplace.demand.runSuccess"));
      await load();
    } catch {
      toast.error(t("marketplace.demand.runFailed"));
    } finally {
      setRunning(false);
    }
  }, [t, load]);

  // Pre-compute previous-snapshot maps for trend Δ.
  const prevBrands = prevSnapshot?.brands || [];
  const prevProductCats = prevSnapshot?.product_categories || [];
  const prevSponsorCats = prevSnapshot?.sponsorship_categories || [];

  const geo = snapshot?.geo_breakdown || [];
  const collapsed = snapshot?.collapsed_to_other_count || 0;

  const collapsedNotice = useMemo(() => {
    if (!collapsed) return null;
    return (t("marketplace.demand.collapsedNotice") || "").replace("{n}", String(collapsed));
  }, [collapsed, t]);

  if (disabled) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="marketplace-demand-disabled">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <h1 className="text-lg font-semibold text-amber-900">
            {t("marketplace.demand.disabledTitle")}
          </h1>
          <p className="mt-2 text-sm text-amber-800">
            {t("marketplace.demand.disabledBody")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="marketplace-demand-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-emerald-700">
            <TrendingUp className="h-5 w-5" />
            <h1 className="text-2xl font-bold text-slate-900 truncate">
              {t("marketplace.demand.pageTitle")}
            </h1>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {t("marketplace.demand.pageSub")}
          </p>
          <div
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] text-emerald-800"
            data-testid="marketplace-demand-privacy-banner"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>{t("marketplace.demand.privacyBanner")}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading || running}
            data-testid="marketplace-demand-refresh"
          >
            <RefreshCw className={`h-4 w-4 me-1.5 ${loading ? "animate-spin" : ""}`} />
            {t("marketplace.demand.refresh")}
          </Button>
          {isAdmin && (
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={loading || running}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="marketplace-demand-run-btn"
            >
              <PlayCircle className="h-4 w-4 me-1.5" />
              {running ? t("marketplace.demand.runningSnapshot") : t("marketplace.demand.runSnapshot")}
            </Button>
          )}
        </div>
      </div>

      {/* Hero */}
      {loading && !snapshot ? (
        <div
          className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400"
          data-testid="marketplace-demand-loading"
        >
          <RefreshCw className="h-5 w-5 mx-auto animate-spin text-emerald-600" />
          <div className="mt-2">…</div>
        </div>
      ) : (
        <DemandSummaryHero snapshot={snapshot} t={t} />
      )}

      {/* Strategic readiness panel — shown whenever the cohort is too
          small to surface any data (insufficient_sample OR no snapshot
          at all). Makes demos read as "Phase 1 staged and waiting"
          rather than "blank screen". */}
      {!loading && (!snapshot || (snapshot.total_users_included ?? 0) < 50) && (
        <DemandReadinessPanel t={t} included={snapshot?.total_users_included ?? 0} />
      )}

      {/* B3.3 Light — WLP adoption funnel mini-dashboard.
          Admin-only (gated by AdminLayout route guards + backend RBAC).
          Reads /api/wlp-adoption/admin/metrics, k-anon enforced server-side. */}
      {isAdmin && <WlpAdoptionPanel t={t} />}

      {/* Three tables — brands / product_cats / sponsor_cats */}
      {snapshot && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <DemandTable
            title={t("marketplace.demand.brandsTitle")}
            items={snapshot.brands}
            prevItems={prevBrands}
            resolveLabel={friendlyLabel}
            t={t}
            testIdSuffix="brands"
          />
          <DemandTable
            title={t("marketplace.demand.productCategoriesTitle")}
            items={snapshot.product_categories}
            prevItems={prevProductCats}
            resolveLabel={friendlyLabel}
            t={t}
            testIdSuffix="product-categories"
          />
          <DemandTable
            title={t("marketplace.demand.sponsorshipCategoriesTitle")}
            items={snapshot.sponsorship_categories}
            prevItems={prevSponsorCats}
            resolveLabel={friendlyLabel}
            t={t}
            testIdSuffix="sponsorship-categories"
          />
        </div>
      )}

      {/* Geo breakdown — country-level only, all K-anon ≥ 5 already */}
      {snapshot && (
        <div
          className="rounded-xl border border-slate-200 bg-white overflow-hidden"
          data-testid="marketplace-demand-geo"
        >
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">
              {t("marketplace.demand.geoTitle")}
            </h3>
          </div>
          {geo.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400" data-testid="marketplace-demand-geo-empty">
              {t("marketplace.demand.geoEmpty")}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {geo.map((g) => (
                <li
                  key={g.country}
                  className="px-4 py-2 flex items-center justify-between"
                  data-testid={`marketplace-demand-geo-${g.country}`}
                >
                  <span className="text-sm text-slate-800 font-mono">{g.country}</span>
                  <span className="font-mono text-xs text-slate-900 font-semibold">{g.users}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Unknown terms */}
      {snapshot && (
        <UnknownTermsPanel items={snapshot.custom_other_top || []} t={t} />
      )}

      {/* Collapsed footer */}
      {snapshot && collapsedNotice && (
        <div
          className="text-xs text-slate-500 text-center italic"
          data-testid="marketplace-demand-collapsed-notice"
        >
          {collapsedNotice}
        </div>
      )}

      {/* Run confirmation dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="marketplace-demand-run-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("marketplace.demand.runConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("marketplace.demand.runConfirmBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="marketplace-demand-run-confirm-no">
              {t("marketplace.demand.runConfirmNo")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runSnapshot}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="marketplace-demand-run-confirm-yes"
            >
              {t("marketplace.demand.runConfirmYes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
